// frontend/src/api/codes.js
import client, { getAccessToken, getCsrfToken } from "./client";

/**
 * Trigger a browser file download from a Blob response.
 *
 * @param {Blob} blob
 * @param {string} filename
 */
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * List QR codes with optional search, type/status filters, and pagination.
 *
 * @param {number} page       1-based page index
 * @param {number} pageSize   Items per page (max 100)
 * @param {string} search     Substring filter on code/name
 * @param {{ qrType?: string, status?: string }} [filters]  Optional filters
 * @returns {Promise<{items, total, page, page_size, pages}>}
 */
export async function listCodes(
  page = 1,
  pageSize = 20,
  search = "",
  { qrType = "", status = "", folderId = "", tagIds = [] } = {},
) {
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (qrType) params.qr_type = qrType;
  if (status) params.status = status;
  if (folderId) params.folder_id = folderId;
  // The backend reads the AND-tag filter from the repeated `tag_ids[]` param.
  if (tagIds && tagIds.length) params["tag_ids[]"] = tagIds;
  const response = await client.get("/api/codes", { params });
  return response.data;
}

/**
 * Apply one action to up to 500 codes atomically (P14-G4).
 *
 * @param {number[]} codeIds
 * @param {"activate"|"deactivate"|"delete"|"move"|"tag"|"untag"} action
 * @param {{ folderId?: string|null, tagId?: string }} [opts]
 * @returns {Promise<{ affected: number }>}
 */
export async function bulkCodes(codeIds, action, { folderId, tagId } = {}) {
  const body = { code_ids: codeIds, action };
  if (folderId !== undefined) body.folder_id = folderId;
  if (tagId !== undefined) body.tag_id = tagId;
  const response = await client.patch("/api/codes/bulk", body);
  return response.data;
}

/**
 * Fetch a single QR code by its identifier.
 *
 * @param {string} code
 * @returns {Promise<object>}
 */
export async function getCode(code) {
  const response = await client.get(`/api/codes/${code}`);
  return response.data;
}

/**
 * Create a new QR code.
 *
 * @param {{ code: string, text: string }} payload
 * @returns {Promise<object>}
 */
export async function createCode(payload) {
  const response = await client.post("/api/codes", payload);
  return response.data;
}

/**
 * Partially update a QR code's text or active status.
 *
 * @param {string} code
 * @param {{ text?: string, is_active?: boolean }} payload
 * @returns {Promise<object>}
 */
export async function updateCode(code, payload) {
  const response = await client.put(`/api/codes/${code}`, payload);
  return response.data;
}

/**
 * Activate a QR code so it accepts public scans (PATCH).
 *
 * @param {string} code
 * @returns {Promise<object>} The updated QRCodeRead
 */
export async function activateCode(code) {
  const response = await client.patch(`/api/codes/${code}/activate`);
  return response.data;
}

/**
 * Deactivate a QR code so public scans return 410 Gone (PATCH).
 *
 * @param {string} code
 * @returns {Promise<object>} The updated QRCodeRead
 */
export async function deactivateCode(code) {
  const response = await client.patch(`/api/codes/${code}/deactivate`);
  return response.data;
}

/**
 * Soft-delete a QR code (the backend sets deleted_at; scan history is kept).
 *
 * @param {string} code
 * @returns {Promise<void>}
 */
export async function deleteCode(code) {
  await client.delete(`/api/codes/${code}`);
}

/**
 * Generate a QR PNG and trigger a browser download.
 *
 * @param {string} code
 * @param {{ size?: number, error_correction?: string }} params
 * @returns {Promise<void>}
 */
export async function downloadQr(code, params = {}) {
  const response = await client.get(`/api/codes/${code}/qr`, {
    params,
    responseType: "blob",
  });
  triggerDownload(response.data, `${code}.png`);
}

/**
 * Bulk-create up to 1 000 QR codes.
 *
 * @param {Array<{ code: string, text: string }>} codes
 * @returns {Promise<object>} QRCodeBatchResult
 */
export async function createBatch(codes) {
  const response = await client.post("/api/codes/batch", { codes });
  return response.data;
}

/**
 * Upload a CSV file and import all valid rows as QR codes.
 *
 * @param {File} file
 * @returns {Promise<object>} QRCodeBatchResult
 */
export async function importCsv(file, qrType = "url") {
  const form = new FormData();
  form.append("file", file);
  form.append("qr_type", qrType);
  const response = await client.post("/api/import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

/**
 * Stream-import a CSV/XLSX file via the SSE endpoint, calling callbacks as
 * events arrive.
 *
 * Callbacks:
 *   onProgress({ batch, rows_in_batch, errors_in_batch })
 *   onRowError({ row, field, message })
 *   onComplete({ inserted, errors })
 *   onError({ message })
 *
 * Throws with err.status = HTTP status on non-200 response (e.g. 402).
 *
 * @param {File}   file
 * @param {string} qrType
 * @param {string} mode    "create" | "upsert" | "skip_duplicates"
 * @param {object} cbs     Callback map
 */
export async function importStream(file, qrType = "url", mode = "create", cbs = {}) {
  const form = new FormData();
  form.append("file", file);
  form.append("qr_type", qrType);
  form.append("mode", mode);

  const headers = {};
  const token = getAccessToken();
  const csrf = getCsrfToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (csrf) headers["X-CSRF-Token"] = csrf;

  const resp = await fetch("/api/import/stream", {
    method: "POST",
    headers,
    body: form,
    credentials: "include",
  });

  if (!resp.ok) {
    let detail = "Import failed.";
    try {
      const body = await resp.json();
      detail = body?.detail ?? detail;
    } catch { /* ignore */ }
    const err = new Error(detail);
    err.status = resp.status;
    throw err;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const frames = buf.split("\n\n");
    buf = frames.pop() ?? "";
    for (const frame of frames) {
      const ev = frame.match(/^event: (.+)$/m)?.[1]?.trim();
      const raw = frame.match(/^data: (.+)$/m)?.[1]?.trim();
      if (!ev || !raw) continue;
      let data;
      try { data = JSON.parse(raw); } catch { continue; }
      if (ev === "progress" && cbs.onProgress) cbs.onProgress(data);
      else if (ev === "row_error" && cbs.onRowError) cbs.onRowError(data);
      else if (ev === "complete" && cbs.onComplete) cbs.onComplete(data);
      else if (ev === "error" && cbs.onError) cbs.onError(data);
    }
  }
}

/**
 * Download the CSV import template for a QR type (type-specific header).
 *
 * @param {string} qrType  QR type to template (default "url")
 * @returns {Promise<void>}
 */
export async function downloadTemplate(qrType = "url") {
  const response = await client.get("/api/import/template", {
    params: { qr_type: qrType },
    responseType: "blob",
  });
  triggerDownload(response.data, `import-template-${qrType}.csv`);
}

/**
 * Build a query-params object from the optional list filters.
 *
 * @param {{ search?: string, qrType?: string, status?: string }} filters
 * @returns {object}
 */
function filterParams({ search = "", qrType = "", status = "" } = {}) {
  const params = {};
  if (search) params.search = search;
  if (qrType) params.qr_type = qrType;
  if (status) params.status = status;
  return params;
}

/**
 * Export codes to CSV (optionally filtered) and trigger a browser download.
 *
 * @param {{ search?: string, qrType?: string, status?: string }} [filters]
 * @returns {Promise<void>}
 */
export async function exportCsv(filters = {}) {
  const response = await client.get("/api/export", {
    params: filterParams(filters),
    responseType: "blob",
  });
  triggerDownload(response.data, "qrflow-codes.csv");
}

/**
 * Download a ZIP of QR code PNGs (optionally filtered) + a manifest.csv.
 *
 * @param {{ search?: string, qrType?: string, status?: string }} [filters]
 * @returns {Promise<void>}
 */
export async function downloadZip(filters = {}) {
  const response = await client.get("/api/export/qr-images", {
    params: filterParams(filters),
    responseType: "blob",
  });
  triggerDownload(response.data, "qrflow-qr-images.zip");
}

/**
 * Kick off an async QR-PNG ZIP export and return the task handle.
 *
 * @returns {Promise<{ task_id: string }>}
 */
export async function startQrZipExport() {
  const resp = await client.post("/api/export/qr-images");
  return resp.data;
}

/**
 * Poll the status of a QR ZIP export task.
 *
 * @param {string} taskId
 * @returns {Promise<{ status: string, download_url?: string }>}
 */
export async function getQrZipExportStatus(taskId) {
  const resp = await client.get(`/api/export/qr-images/${taskId}`);
  return resp.data;
}

/**
 * Kick off an async GDPR data export (email notification when ready).
 *
 * @returns {Promise<{ message: string, task_id: string }>}
 */
export async function startGdprExport() {
  const resp = await client.post("/api/export/gdpr");
  return resp.data;
}
